import { useEffect, useState } from 'react';
import './style.scss';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Tooltip from 'bootstrap/js/dist/tooltip';
import { Link } from 'react-router-dom';
import Alert from '../../../../../../Shared/Alert';
import { Status } from '../../../../../../../hooks/fetch.hook';
import { AnalyticsService } from '../../../../../../../services/analytics-service';
import FocusTrap from '../../../../../../Shared/FocusTrap';
import type IMunicipalityAdministratorParticipation from '../../models/participation-list-entry.view-model';
import exclamationCircleRed from '../../../../../../../../assets/icons/exclamation-circle-red-600.svg';
import { TranslationService } from '../../../../../../../services/translation.service';
import { ParticipationHelper } from '../../../../../helpers/participation.helper';
import ListItemDropDownMenu from '../../../../../Shared/ListItemDropDownMenu';
import { MenuItemType } from '../../../../../Shared/ListItemDropDownMenu/menu-item-type.enum';
import AntiforgeryService from '../../../../../../../services/anti-forgery.service';

function Participations({
  participations,
  anyLegIsActive,
  onDeleted,
}: {
  participations: IMunicipalityAdministratorParticipation[];
  anyLegIsActive: boolean;
  onDeleted: (deletedParticipation: IMunicipalityAdministratorParticipation) => void;
}) {
  // === STATE ===
  const [deleteParticipationStatus, setDeleteParticipationStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });
  const [selectedParticipation, setSelectedParticipation] = useState<IMunicipalityAdministratorParticipation | undefined>(undefined);
  const [modalTriggerElement, setModalTriggerElement] = useState<string>('');

  useEffect(() => {
    // Initialize Bootstrap tooltips after the component is mounted
    const tooltipTriggerElementsList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    const tooltipTriggerList = [...tooltipTriggerElementsList].map((tooltipTriggerEl: string | Element) => new Tooltip(tooltipTriggerEl));

    // Clean up the tooltips when the component is unmounted
    return () => {
      tooltipTriggerList.forEach((tooltipTrigger) => tooltipTrigger.dispose());
    };
  }, [participations]);

  useEffect(() => {
    // Initialize Bootstrap dropdowns after the component is mounted
    const dropdownElementList = document.querySelectorAll('.dropdown-toggle');
    const dropdownList = [...dropdownElementList].map((dropdownToggleEl) => new Dropdown(dropdownToggleEl));

    // Clean up the dropdowns when the component is unmounted
    return () => {
      dropdownList.forEach((dropdown) => dropdown.dispose());
    };
  }, []);

  async function handleConfirmDeleteClick() {
    if (!selectedParticipation) return;

    try {
      setDeleteParticipationStatus({ status: Status.notperformed, statusCode: 0 });
      const response = await fetch(`/api/paegnaben/municipality-administrator/participations/${selectedParticipation.participationId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      });

      setDeleteParticipationStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
      if (response.ok) {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Klass borttagen', '');
        onDeleted(selectedParticipation);
      } else {
        AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Klass borttagen - misslyckat', '');
      }
      setSelectedParticipation(undefined);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setDeleteParticipationStatus({ status: Status.failed, statusCode: error.status });
    }
    setModalTriggerElement('');
  }

  function handleOnClose() {
    setSelectedParticipation(undefined);
    if (modalTriggerElement) {
      document.getElementById(modalTriggerElement)?.focus();
    }
  }

  function renderPreventDeleteModalContent() {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="sign-modal-title" className="modal-title fs-3">
            Det går inte att ta bort klassen
          </h2>
          <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
        </div>
        <div className="modal-body pt-0">
          <div className="mb-3">
            <p>
              <strong>
                {selectedParticipation?.participationName}, {selectedParticipation?.schoolName}
              </strong>{' '}
              har rapporterat in poäng och kan därför inte tas bort.
            </p>
            <p>Kontakta Västtrafiks projektledare för att få hjälp med att ta bort klassen.</p>
          </div>
          <div className="d-grid gap-3">
            <button type="button" className="btn btn-primary" onClick={handleOnClose}>
              Ok
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderDeleteModalContent() {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h2 id="sign-modal-title" className="modal-title fs-3">
            Ta bort klass
          </h2>
          <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
        </div>
        <div className="modal-body pt-0">
          <div className="mb-3">
            Vill du ta bort{' '}
            <strong>
              {selectedParticipation?.participationName}, {selectedParticipation?.schoolName}
            </strong>
            ?
          </div>
          <div className="d-grid d-md-flex gap-3">
            <button type="button" className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1" onClick={handleOnClose}>
              Avbryt
            </button>
            <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleConfirmDeleteClick}>
              Ta bort
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {selectedParticipation && (
        <FocusTrap isVisible={!!selectedParticipation}>
          <div className="modal d-block fade show" aria-labelledby="sign-modal-title" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered">{selectedParticipation.hasLegReports ? renderPreventDeleteModalContent() : renderDeleteModalContent()}</div>
          </div>
        </FocusTrap>
      )}
      {selectedParticipation && <div className="modal-backdrop fade show"></div>}
      <div className="participations">
        <table className="table w-100">
          <thead>
            <tr className="participation-row d-flex justify-content-between">
              <th scope="col" className="participation-row__table-data">
                Tävlingsställning
              </th>
              <th scope="col" className="participation-row__table-data name flex-grow-1">
                <span className="visually-hidden">Skola</span>
              </th>
              <th scope="col" className="participation-row__table-data warnings">
                <span className="visually-hidden">Varningar</span>
              </th>
              <th scope="col" className="participation-row__table-data text-end score-heading text-center ps-0">
                Etappoäng
              </th>
              <th scope="col" className="participation-row__table-data score-heading total ps-0">
                Totalt
              </th>
              <th scope="col" className="participation-row__table-data menu visually-hidden">
                Meny
              </th>
            </tr>
          </thead>
          <tbody>
            {participations.length === 0 ? (
              <tr>
                <td colSpan={6}>Inga klasser matchar filtret</td>
              </tr>
            ) : (
              participations.map((item) => (
                <tr key={item.participationId} id={item.participationId.toString()} className="d-flex justify-content-between participation-row" tabIndex={-1}>
                  <td className="d-flex align-items-center justify-content-center participation-row__table-data rank">
                    <span>{item.rank}</span>
                  </td>
                  <td className="d-flex align-items-center participation-row__table-data name flex-grow-1 text-break">
                    <Link to={`/klasser/${item.participationId}`}>
                      {item.participationName} {item.schoolName}
                    </Link>
                  </td>
                  <td className="participation-row__table-data warnings d-flex align-items-center">
                    {ParticipationHelper.missingLegScore(item, anyLegIsActive) && (
                      <img data-bs-toggle="tooltip" data-bs-placement="top" alt="" data-bs-offset={[0, 0]} title="Etappoäng saknas" src={exclamationCircleRed} aria-label="Etappoäng saknas" />
                    )}
                  </td>
                  <td
                    className={`participation-row__table-data text-end points d-flex align-items-center ${
                      ParticipationHelper.missingLegScore(item, anyLegIsActive) ? 'missed-qualification ps-0' : ''
                    }`}
                  >
                    <span>{item.latestFinishedLegReportWeightedScore}</span>
                  </td>
                  <td className="participation-row__table-data text-end total-points d-flex align-items-center">
                    <span>{item.weightedTotalScore}</span>
                  </td>
                  <td className="participation-row__table-data p-0 d-flex align-items-center menu">
                    <ListItemDropDownMenu
                      menuItems={[
                        {
                          type: MenuItemType.Delete,
                          label: 'Ta bort',
                          onClick: () => {
                            setModalTriggerElement(item.participationId.toString());
                            setSelectedParticipation(item);
                          },
                        },
                        {
                          type: MenuItemType.Edit,
                          label: 'Redigera',
                          link: `/klasser/${item.participationId}/klassuppgifter`,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {deleteParticipationStatus.status === Status.failed && <Alert show={true} heading="Kunde inte ta bort klassen" closable={false} className="mb-4" />}
      </div>
    </>
  );
}
export default Participations;